import type { ReactElement, ReactNode } from 'react';
import { BordOneToneIcon } from 'bord-design-system';
import NudosButton from 'components/NudosComponents/NudosButton/NudosButton';
import type { IInspectionImage } from 'types/suscription/toolInspection.types';
import { bordColorTheme } from 'styles/styles.constants';

import './EvidenceGallery.scss';

interface IEvidenceGalleryProps {
  photos: IInspectionImage[];
  onAddPhotos?: () => void;
  onChangePhotos: () => void;
  onThumbnailClick: (photo: IInspectionImage) => void;
  isDisabled?: boolean;
  isAddFlowBusy?: boolean;
}

const EvidenceGallery = ({
  photos,
  onAddPhotos,
  onChangePhotos,
  onThumbnailClick,
  isDisabled,
  isAddFlowBusy
}: IEvidenceGalleryProps) => {
  const handleAddPhotos = onAddPhotos ?? onChangePhotos;
  const photosWithUrl = photos.flatMap(photo => {
    const url = photo.url?.trim();
    return url ? [{ ...photo, url }] : [];
  });

  const hasPhotos = photosWithUrl.length > 0;
  const isActionDisabled = Boolean(isDisabled) || Boolean(isAddFlowBusy);

  const renderGalleryCells = (): ReactNode => {
    if (!hasPhotos) {
      return (
        <div className="galleryEmptySlot" aria-hidden>
          <BordOneToneIcon variant="plus" standardSize={12} stroke={bordColorTheme.navyBlue[900]} />
        </div>
      );
    }

    return photosWithUrl.map((photo, index) => (
      <button
        key={`${photo.imageSlot}-${photo.id ?? index}`}
        type="button"
        className="galleryThumb"
        onClick={() => onThumbnailClick(photo)}
      >
        <img src={photo.url} alt="" className="galleryThumbImage" />
      </button>
    ));
  };

  const renderGalleryActions = (): ReactElement => {
    if (!hasPhotos) {
      return (
        <NudosButton
          colorPalette={isActionDisabled ? 'disabled' : 'secondary'}
          buttonText="Agregar imágenes"
          handleClick={handleAddPhotos}
          isButtonDisabled={isActionDisabled}
          customClassName="evidenceGalleryChangePhotosNudos"
        />
      );
    }

    return (
      <NudosButton
        colorPalette={isActionDisabled ? 'disabled' : 'secondary'}
        buttonText="Cambiar fotos subidas"
        buttonIcon={
          <BordOneToneIcon
            variant="refreshCw"
            standardSize={12}
            stroke={isActionDisabled ? bordColorTheme.navyBlue[400] : bordColorTheme.navyBlue[900]}
          />
        }
        handleClick={onChangePhotos}
        isButtonDisabled={isActionDisabled}
        customClassName="evidenceGalleryChangePhotosNudos"
      />
    );
  };

  return (
    <div className="evidenceGalleryContainer">
      <p className="galleryTitle">Fotos de la herramienta (Evidencia)</p>

      <div className="galleryThumbsTrack">
        <div className="galleryRow">{renderGalleryCells()}</div>
      </div>

      <div className="galleryActions">{renderGalleryActions()}</div>
    </div>
  );
};

export default EvidenceGallery;
